/**
 * Файлы заявок «Помещение» в вики (партнёр): какую ссылку на папку показать и по какой ссылке открыть
 * файл. Чистые правила без DOM и сети (тест — room-files.test.ts). Копия правил CRM:
 * frontend/src/lib/room-files.ts; план — docs/plans/ROOM_FILES_GOOGLE_DRIVE_PLAN.md.
 *
 * Новые записи payload (с 28.10.2026) лежат в нашем хранилище и уходят на Google Диск в фоне:
 * `{ name, url, fileId, storage: 'crm', driveUrl? }`. `url` у них — путь API CRM для сотрудников
 * (право `forms:view`), партнёру он не открывается: партнёр получает только `driveUrl` после выгрузки.
 * Старые записи — публичные ссылки Яндекс.Диска (`url`) без `storage`.
 */

export interface RoomFileEntry {
  name?: string;
  url?: string | null;
  path?: string | null;
  fileId?: string;
  storage?: string;
  driveUrl?: string | null;
}

/** Что показывать вместо ссылки на папку. */
export type RoomFolderKind = 'google' | 'pending' | 'yandex' | 'none';

export interface RoomFolderLink {
  kind: RoomFolderKind;
  /** google — папка помещения на Google Диске; yandex — старая папка Яндекс.Диска. */
  url?: string;
  /** Заявка смешанная (старые фото на Яндекс.Диске + новые у нас): старая папка остаётся доступной. */
  legacyUrl?: string;
}

const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v.trim() : null);

/** Запись из нового хранилища (CRM + Google Диск)? */
export function isCrmStored(entry: unknown): boolean {
  return !!entry && typeof entry === 'object' && (entry as RoomFileEntry).storage === 'crm';
}

function allEntries(payload: Record<string, unknown>): unknown[] {
  const list: unknown[] = [];
  if (Array.isArray(payload.photoLinks)) list.push(...payload.photoLinks);
  if (Array.isArray(payload.projectPlan)) list.push(...payload.projectPlan);
  if (payload.project && typeof payload.project === 'object') list.push(payload.project);
  return list;
}

/**
 * Ссылка на папку с фото заявки:
 *  • есть `driveFolderUrl` → `google` («Папка с фото на Google Диске»);
 *  • иначе есть записи нового формата → `pending` («Фото выгружаются на Google Диск»);
 *  • иначе есть старая `photoFolderUrl` → `yandex` (как раньше);
 *  • иначе `none`.
 */
export function roomFolderLink(payload: Record<string, unknown> | null | undefined): RoomFolderLink {
  const p = payload ?? {};
  const driveFolder = str(p.driveFolderUrl);
  const yandex = str(p.photoFolderUrl);
  const hasCrm = allEntries(p).some(isCrmStored);
  const hasLegacyPhotos = Array.isArray(p.photoLinks) && p.photoLinks.some((e) => !isCrmStored(e));
  const legacyUrl = yandex && hasLegacyPhotos && (driveFolder || hasCrm) ? yandex : undefined;

  if (driveFolder) return { kind: 'google', url: driveFolder, ...(legacyUrl ? { legacyUrl } : {}) };
  if (hasCrm) return { kind: 'pending', ...(legacyUrl ? { legacyUrl } : {}) };
  if (yandex) return { kind: 'yandex', url: yandex };
  return { kind: 'none' };
}

export interface PartnerFileLink {
  /** Ссылка, которая откроется у партнёра; null — открывать нечем. */
  href: string | null;
  /** Новый файл ещё не на Google Диске: показываем «загружается». */
  pending: boolean;
}

/**
 * Ссылка на файл для партнёра: `driveUrl ?? url`, но `url` берём только публичный (http/https, старый
 * Яндекс.Диск). Путь API CRM у нового файла партнёру не открывается — пока нет `driveUrl`, файл «загружается».
 */
export function partnerFileLink(entry: RoomFileEntry | null | undefined): PartnerFileLink {
  if (!entry) return { href: null, pending: false };
  const drive = str(entry.driveUrl);
  if (drive) return { href: drive, pending: false };
  if (isCrmStored(entry)) return { href: null, pending: true };
  const url = str(entry.url);
  return { href: url && /^https?:\/\//i.test(url) ? url : null, pending: false };
}
